import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/esm/Button";
import SundayService from "../DTOs/SundayService";
import Preacher from "../DTOs/Preacher";
import { getSlotAlert } from "../utils/slotStatus";
import { preacherDisplayName } from "../utils/preachers";

interface CalendarViewProps {
    services: (SundayService & { id: string })[];
    preachers: Preacher[];
    currentMonth: Date;
    onPreviousMonth: () => void;
    onNextMonth: () => void;
    selectedDate: Date | null;
    onSelectDate: (date: Date) => void;
    /** Missing for members: they may not create services (GOD-55). */
    onAddServiceForDate?: (date: Date) => void;
    onEditService: (service: SundayService & { id: string }) => void;
    /** Holiday names per day (YYYY-MM-DD), see utils/germanHolidays (GOD-50). */
    holidays?: Map<string, string[]>;
}

const WEEKDAY_SHORT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function CalendarView({
    services,
    preachers,
    currentMonth,
    onPreviousMonth,
    onNextMonth,
    selectedDate,
    onSelectDate,
    onAddServiceForDate,
    onEditService,
    holidays,
}: CalendarViewProps) {
    const { t, i18n } = useTranslation();
    const locale = (i18n.resolvedLanguage || i18n.language || "de").startsWith("en") ? "en-GB" : "de-DE";
    const weekdayShort = locale === "de-DE" ? WEEKDAY_SHORT : ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
    const holidaysOf = (date: Date) =>
        holidays?.get(
            `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
        ) ?? [];

    const preacherMap = useMemo(() => {
        const map = new Map<string, string>();
        preachers.forEach((p) => map.set(p.key, `${p.name} ${p.surname}`));
        return map;
    }, [preachers]);

    const getPreacherName = (key: string) => preacherDisplayName(key, preacherMap, t("common.deletedPreacher"));
    // What an entry shows: the first preacher, else the service's name, else "open" – never an empty bar (GOD-55).
    const entryText = (service: SundayService) =>
        (service.sermon1PreacherKey && getPreacherName(service.sermon1PreacherKey)) ||
        (service.sermon2PreacherKey && getPreacherName(service.sermon2PreacherKey)) ||
        service.serviceLabel ||
        (t(`serviceTypes.${service.serviceType || "manual"}`, { defaultValue: "" }) as string) ||
        (t("calendar.open") as string);

    const calendarData = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        let startDayOfWeek = firstDay.getDay() - 1;
        if (startDayOfWeek === -1) startDayOfWeek = 6;

        const daysInMonth = lastDay.getDate();
        const totalCells = Math.ceil((startDayOfWeek + daysInMonth) / 7) * 7;
        const daysFromNextMonth = totalCells - startDayOfWeek - daysInMonth;

        const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];
        const prevLastDay = new Date(year, month, 0);
        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            days.push({ date: new Date(year, month - 1, prevLastDay.getDate() - i), isCurrentMonth: false });
        }
        for (let i = 1; i <= daysInMonth; i++) {
            days.push({ date: new Date(year, month, i), isCurrentMonth: true });
        }
        for (let i = 1; i <= daysFromNextMonth; i++) {
            days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
        }
        return days;
    }, [currentMonth]);

    const servicesByDate = useMemo(() => {
        const map = new Map<string, (SundayService & { id: string })[]>();
        services.forEach((s) => {
            const d = new Date(s.date);
            const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(s);
        });
        return map;
    }, [services]);

    const getServicesForDate = (date: Date) => {
        const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        return servicesByDate.get(key) || [];
    };

    const today = new Date();
    const isToday = (date: Date) =>
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

    const isSameDay = (a: Date, b: Date) =>
        a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

    const inShownMonth = (date: Date) =>
        date.getMonth() === currentMonth.getMonth() && date.getFullYear() === currentMonth.getFullYear();
    const agendaDate =
        selectedDate && inShownMonth(selectedDate)
            ? selectedDate
            : inShownMonth(today)
              ? today
              : new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);

    const monthYearText = currentMonth.toLocaleDateString(locale, { month: "long", year: "numeric" });

    const weeks: Array<typeof calendarData> = [];
    for (let i = 0; i < calendarData.length; i += 7) {
        weeks.push(calendarData.slice(i, i + 7));
    }

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-3">
                    <Button variant="ghost" size="sm" className="btn-ghost" onClick={onPreviousMonth}>‹</Button>
                    <h4 className="m-0">{monthYearText}</h4>
                    <Button variant="ghost" size="sm" className="btn-ghost" onClick={onNextMonth}>›</Button>
                </div>
                {selectedDate && onAddServiceForDate && (
                    <Button variant="primary" size="sm" className="text-nowrap" onClick={() => onAddServiceForDate(selectedDate)}>
                        + {t("simpleSundayService.addNew")} ({selectedDate.toLocaleDateString(locale, { day: "numeric", month: "short" })})
                    </Button>
                )}
            </div>

            <div className="gl-cal">
                <div className="gl-cal-head">
                    {weekdayShort.map((d) => <div key={d}>{d}</div>)}
                </div>
                {weeks.map((week, wi) => (
                    <div className="gl-cal-row" key={wi}>
                        {week.map((day, di) => {
                            const dayServices = getServicesForDate(day.date);
                            const todayDay = isToday(day.date);
                            const isSelected = selectedDate ? isSameDay(selectedDate, day.date) : false;

                            let cls = "gl-cal-day";
                            if (!day.isCurrentMonth) cls += " is-other";
                            if (todayDay) cls += " is-today";
                            if (isSelected) cls += " is-selected";

                            return (
                                <div
                                    key={di}
                                    className={cls}
                                    onClick={() => onSelectDate(day.date)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectDate(day.date); } }}
                                >
                                    <span className="gl-cal-num">{day.date.getDate()}</span>
                                    {holidaysOf(day.date).map((name) => (
                                        <span key={name} className="gl-cal-holiday" title={name}>
                                            {name}
                                        </span>
                                    ))}
                                    {dayServices.map((service) => {
                                        const label = service.serviceLabel || service.serviceType || "";
                                        const p1 = entryText(service);
                                        // A preacher declined or asked for a swap: needs the admin's eye.
                                        const alert = getSlotAlert(service, 1) ?? getSlotAlert(service, 2);
                                        return (
                                            <span
                                                key={service.id}
                                                className={`gl-cal-evt${alert ? " gl-cal-evt--warn" : ""}`}
                                                onClick={(e) => { e.stopPropagation(); onEditService(service); }}
                                                title={
                                                    alert
                                                        ? `${label || p1} – ${t(alert === "declined" ? "swap.calendarDeclined" : "swap.calendarSwapPending")}`
                                                        : label || p1
                                                }
                                            >
                                                {alert === "declined" ? "⚠ " : alert === "swap-pending" ? "⇄ " : ""}
                                                {p1}
                                            </span>
                                        );
                                    })}
                                    {(dayServices.length > 0 || holidaysOf(day.date).length > 0) && (
                                        <span className="gl-cal-dots" aria-hidden="true">
                                            {holidaysOf(day.date).length > 0 && <span className="gl-cal-dot gl-cal-dot--holiday" />}
                                            {dayServices.map((service) => (
                                                <span
                                                    key={service.id}
                                                    className={`gl-cal-dot${
                                                        getSlotAlert(service, 1) ?? getSlotAlert(service, 2) ? " gl-cal-dot--warn" : ""
                                                    }`}
                                                />
                                            ))}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Phones: the chosen day (else today in this month, else the 1st) as a list (GOD-55). */}
            <div className="gl-cal-agenda" data-testid="cal-agenda">
                <div className="gl-meta mb-2">
                    {agendaDate.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}
                </div>
                {holidaysOf(agendaDate).map((name) => (
                    <div key={name} className="gl-cal-holiday mb-2">
                        {name}
                    </div>
                ))}
                {getServicesForDate(agendaDate).length === 0 && holidaysOf(agendaDate).length === 0 && (
                    <p className="text-muted small mb-0">{t("calendar.nothingOnDay")}</p>
                )}
                {getServicesForDate(agendaDate).map((service) => {
                    const alert = getSlotAlert(service, 1) ?? getSlotAlert(service, 2);
                    return (
                        <button
                            key={service.id}
                            type="button"
                            className={`gl-cal-agenda-item${alert ? " gl-cal-evt--warn" : ""}`}
                            onClick={() => onEditService(service)}
                        >
                            <span className="fw-semibold">
                                {service.serviceLabel || t(`serviceTypes.${service.serviceType || "manual"}`)}
                            </span>
                            <span className="text-muted small">{entryText(service)}</span>
                        </button>
                    );
                })}
            </div>

            <div className="d-flex gap-3 mt-3">
                <span className="gl-meta d-flex align-items-center gap-2">
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: "var(--gl-accent-100)", borderLeft: "2px solid var(--gl-accent)", display: "inline-block" }} />
                    {t("calendar.plannedServiceIndicator")}
                </span>
                <span className="gl-meta d-flex align-items-center gap-2">
                    <span style={{ width: 10, height: 10, borderRadius: "999px", background: "var(--gl-accent)", display: "inline-block" }} />
                    {t("calendar.todayIndicator")}
                </span>
                {holidays && holidays.size > 0 && (
                    <span className="gl-meta d-flex align-items-center gap-2">
                        <span className="gl-cal-holiday">{t("calendar.holidayIndicator")}</span>
                    </span>
                )}
            </div>
        </div>
    );
}
